/*  ========================================================================
    # App
    ========================================================================  */

// Dependencies
import React, { useRef } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import PropTypes from "prop-types";
import SubmissionContextProvider from "./utils/SubmissionContextProvider";

// Pages
import Home from "@pages/Home";
import Submit from "@pages/Submit";
import FourOhFour from "@pages/404";
import Modal from "./components/Modal";

// Global Data Context
export const IdeaContext = React.createContext([]);
export const TermsContext = React.createContext(null);

const App = ({ ideas }) => {
  const location = useLocation();
  const termsModalRef = useRef(null);

  return (
    <TermsContext.Provider value={termsModalRef}>
      <IdeaContext.Provider value={ideas}>
        <SubmissionContextProvider>
          <AnimatePresence>
            <Routes location={location} key={location.pathname}>
              <Route index element={<Home />} />
              {/* <Route path='mitmachen' element={<Submit />} /> */}
              <Route path="*" element={<FourOhFour />} />
            </Routes>
          </AnimatePresence>

          <Modal ref={termsModalRef}>
            <h3 className="c-terms-modal__headline">
              Raiffeisen-Förderpreis 2025
              <br />
              Teilnahmebedingungen
            </h3>
            <ol style={{ listStyle: "decimal-leading-zero" }}>
              <li>
                Dieser Wettbewerb wird von der Deutschen
                Friedrich-Wilhelm-Raiffeisen-Gesellschaft e.V. veranstaltet, im
                Folgenden Veranstalter genannt.
              </li>
              <li>
                Teilnahmeberechtigt sind ausschließlich Teilnehmer mit ständigem
                Wohnsitz in Deutschland, die zum Zeitpunkt der Ideeneinreichung
                zwischen 18 und 29 Jahren alt sind.
              </li>
              <li>
                Die Teilnahme ist unentgeltlich, freiwillig und unabhängig vom
                Erwerb von Waren und Dienstleistungen. Die Teilnahme ist
                ausschließlich über die Web-Plattform{" "}
                <a
                  href="https://www.raiffeisen-foerderpreis.de"
                  title="www.raiffeisen-foerderpreis.de"
                  className="inline-link"
                >
                  www.raiffeisen-foerderpreis.de
                </a>{" "}
                möglich.
              </li>
              <li>
                Der Wettbewerb ist in mehrere Wettbewerbsphasen gegliedert.
                <ol style={{ listStyle: "lower-latin" }}>
                  <li>
                    Die Einsendephase beginnt am 31. März 2025 und endet am 30.
                    Juni 2025. Bis dahin ist es den Teilnehmern möglich, ihre
                    Ideen einzureichen.
                  </li>
                  <li>
                    Im Anschluss wird der Veranstalter bestimmen, welche 10
                    Teilnehmer sich für die Präsentation vor der Jury
                    qualifizieren. Dabei werden insbesondere die Kriterien
                    Nutzwert, Innovationscharakter und Realisierbarkeit
                    berücksichtigt.
                  </li>
                  <li>
                    Diese Teilnehmer werden nach Beendigung der
                    Einreichungsphase zeitnah kontaktiert und dürfen ihre Ideen
                    am 14. August 2025 in einer virtuellen Präsentation der Jury
                    vorstellen. Der Veranstalter behält sich das Recht vor, nur
                    Teilnehmer als mögliche Gewinner zu berücksichtigen, die
                    ihre Ideen bei der Jurysitzung am 14. August vorstellen. Die
                    Teilnehmer sollten deswegen sicherstellen, dass sie am 14.
                    August von 14:00 bis 17:00 Uhr verfügbar sind.
                  </li>
                  <li>
                    Die Jury wird anschließend den 1. Platz, den 2. Platz und
                    den 3. Platz bestimmen.
                  </li>
                  <li>
                    Die Preisverleihung für die drei Gewinner erfolgt am 17.
                    September 2025 in Frankfurt am Main.
                  </li>
                </ol>
              </li>
              <li>
                Um die prämierten Ideen zu fördern, erhalten die Gewinner
                Geldpreise in folgender Höhe:
                <br />
                1. Platz: 5 000 Euro
                <br />
                2. Platz: 3 000 Euro
                <br />
                3. Platz: 2 000 Euro
                <br />
                Gewinnansprüche sind nicht übertragbar. Es besteht kein Anspruch
                auf die Umsetzung der Gewinnerideen durch den Veranstalter. Der
                Rechtsweg ist ausgeschlossen. Das Preisgeld wird den Gewinnern
                per Überweisung übermittelt.
              </li>
              <li>
                Die bereitgestellten persönlichen Informationen werden für die
                Kontaktierung der Teilnehmer im Falle des Erreichens der jeweils
                nächsten Wettbewerbsphase verwendet. Mit dem Einreichen der Idee
                stimmen die Teilnehmer zudem der Veröffentlichung der Idee auf
                der Website{" "}
                <a
                  href="https://www.raiffeisen-foerderpreis.de"
                  title="www.raiffeisen-foerderpreis.de"
                  className="inline-link"
                >
                  www.raiffeisen-foerderpreis.de
                </a>{" "}
                sowie der weiteren Verwendung im Kontext des Ideenwettbewerbs
                und dessen Bewerbung zu. Dies gilt auch für Bilder, die im
                Rahmen der Preisverleihung gemacht werden.
              </li>
              <li>
                Die Verwendung mehrerer E-Mail-Adressen oder eine mehrfache
                Teilnahme zur Erhöhung der Gewinnchancen ist nicht erlaubt. Der
                Veranstalter behält sich das Recht vor, Teilnehmer vom
                Wettbewerb auszuschließen, die gegen die Teilnahmebedingungen
                verstoßen.
              </li>
              <li>
                Ausschließlich anwendbares Recht ist das Recht der
                Bundesrepublik Deutschland. Sollten einzelne dieser Bestimmungen
                unwirksam sein oder werden, bleibt die Gültigkeit der übrigen
                Teilnahmebedingungen hiervon unberührt. Der Rechtsweg ist
                ausgeschlossen.
              </li>
              <li>
                Der Veranstalter behält sich vor, den Wettbewerb jederzeit ohne
                Vorankündigung und ohne Angabe von Gründen abzubrechen oder zu
                beenden. Dies gilt insbesondere, falls eine ordnungsgemäße
                Durchführung aus technischen oder rechtlichen Gründen nicht
                gewährleistet werden kann. Den Teilnehmern stehen in einem
                solchen Fall keinerlei Ansprüche gegenüber dem Veranstalter zu.
              </li>
              <li>
                Sollte sich ein Teilnehmer im Laufe des Wettbewerbs dazu
                entscheiden, nicht weiter am Wettbewerb teilzunehmen, ist dies
                jederzeit über eine Kontaktaufnahme an{" "}
                <a href="mailto:kira.meier@dzbank.de" className="inline-link">
                  kira.meier@dzbank.de
                </a>{" "}
                oder{" "}
                <a
                  href="mailto:malin.aussenhofer@dzbank.de"
                  className="inline-link"
                >
                  malin.aussenhofer@dzbank.de
                </a>{" "}
                möglich.
              </li>
            </ol>
          </Modal>
        </SubmissionContextProvider>
      </IdeaContext.Provider>
    </TermsContext.Provider>
  );
};

App.propTypes = {
  ideas: PropTypes.arrayOf(PropTypes.object),
};

export default App;
